import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { baseURL } from '../Constants';
import axios from 'axios';
import '../styles/components/edit-profile-page.scss';
import { FaUser, FaIdCard, FaCalendarAlt, FaUserTag, FaTwitter, FaSteam, FaGamepad, FaCheck, FaTimes, FaUserEdit } from 'react-icons/fa';
import { SiFaceit } from 'react-icons/si';

// Updated interface to include dateOfBirth instead of age
interface ProfileFormData {
  steamID: string;
  playerName: string;
  name?: string;
  dateOfBirth?: string; // Changed from age to dateOfBirth
  lft?: boolean;
  twitter?: string;
  faceit?: string;
  role?: string;
  team?: any; // Preserve team data if present
  discordId?: string;
  discordUsername?: string;
  isAdmin?: boolean; // Preserve admin status
  passwordHash?: string | null; // Preserve password hash
}

const EditProfilePage: React.FC = () => {
  const { steamId } = useParams<{ steamId: string }>();
  const { user, token, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<ProfileFormData>({
    steamID: steamId || '',
    playerName: '',
    name: '',
    dateOfBirth: '', // Initialize with empty string
    lft: false,
    twitter: '',
    faceit: '',
    role: '',
    discordId: '',
    discordUsername: '',
    isAdmin: false, // Initialize to false
    passwordHash: null, // Initialize to null
  });
  
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  // Format date to YYYY-MM-DD for input[type="date"]
  const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return '';
    
    // If it's already in the right format, just return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    
    // Otherwise try to parse and format the date
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (err) {
      return '';
    }
  };
  
  // Fetch current profile data
  useEffect(() => {
    // Set page title
    document.title = 'Edit Profile | Northern Championships Series';
    
    const fetchProfile = async () => {
      if (!steamId) return;
      
      try {
        setLoading(true);
        const response = await axios.get(`${baseURL}/Account/${steamId}`);
        const profileData = response.data;

        setFormData({
          steamID: profileData.steamID || steamId,
          playerName: profileData.playerName || '',
          name: profileData.name || '',
          dateOfBirth: formatDateForInput(profileData.dateOfBirth), // Format the date
          lft: profileData.lft || false,
          twitter: profileData.twitter || '',
          faceit: profileData.faceit || '',
          role: profileData.role || '',
          team: profileData.team, // Preserve team data
          discordId: profileData.discordId || '',
          discordUsername: profileData.discordUsername || '',
          isAdmin: profileData.isAdmin || false, // Preserve admin status
          passwordHash: profileData.passwordHash || null, // Preserve password hash
        });
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data. Please try again.');
        
        // Fallback to user context data if API fails
        if (user) {
          setFormData({
            steamID: user.steamID || steamId || '',
            playerName: user.playerName || '',
            name: user.name || '',
            dateOfBirth: formatDateForInput(user.dateOfBirth), // Format the date from user context
            lft: user.lft || false,
            twitter: user.twitter || '',
            faceit: '',
            role: user.role || '',
            discordId: '',
            discordUsername: '',
            isAdmin: user.isAdmin || false, // Preserve admin status
            passwordHash: null, // Don't use from context for security
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [steamId, user]);
  
  // Verify user has permission to edit this profile (own profile or admin)
  useEffect(() => {
    if (!loading && user && steamId !== user.steamID && !isAdmin) {
      setError('You can only edit your own profile');
      setTimeout(() => {
        navigate(`/profile/${steamId}`);
      }, 2000);
    }
  }, [user, steamId, navigate, loading, isAdmin]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      // Ensure we have the token for authentication
      if (!token) {
        throw new Error('You must be logged in to update your profile');
      }

      // Ensure playerName is not empty
      if (!formData.playerName.trim()) {
        throw new Error('Player Name is required');
      }
      
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Convert dateOfBirth to proper ISO format for API
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null
      };
      
      // API call to update profile
      await axios.put(`${baseURL}/Account/${steamId}`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setSuccess(true);
      
      // Navigate back to profile after short delay
      setTimeout(() => {
        navigate(`/profile/${steamId}`);
      }, 1500);
      
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data || err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="edit-profile-loading">
        <div className="loading-spinner"></div>
        <h2>Loading profile data...</h2>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="edit-profile-error">
        <h2>Access Denied</h2>
        <p>You must be logged in to edit this profile.</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Return to Home
        </button>
      </div>
    );
  }
  
  return (
    <div className="edit-profile-page">
      <div className="container">
        <div className="edit-profile-header">
          <h1><FaUserEdit /> Edit Profile</h1>
          <p>Update your player information</p>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            <FaTimes /> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <FaCheck /> Profile updated successfully!
          </div>
        )}
        
        <div className="edit-profile-content">
          <form onSubmit={handleSubmit} className="edit-profile-form">
            <div className="form-group">
              <label htmlFor="playerName">
                <FaUser /> Player Name
              </label>
              <input
                type="text"
                id="playerName"
                name="playerName"
                value={formData.playerName}
                onChange={handleChange}
                placeholder="Your in-game name"
                required
              />
              <span className="form-hint">This is how other players will see you</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="name">
                <FaIdCard /> Real Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your real name (optional)"
              />
              <span className="form-hint">Optional - share your real name if you wish</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="dateOfBirth">
                <FaCalendarAlt /> Date of Birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]} // Prevent future dates
              />
              <span className="form-hint">Your birth date (optional) - must be at least 13 years old</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="role">
                <FaUserTag /> Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="">Select your primary role</option>
                <option value="Entry Fragger">Entry Fragger</option>
                <option value="Support">Support</option>
                <option value="IGL">In-Game Leader</option>
                <option value="Lurker">Lurker</option>
                <option value="AWPer">AWPer</option>
                <option value="Rifler">Rifler</option>
                <option value="Coach">Coach</option>
              </select>
              <span className="form-hint">Your primary role in a team</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="twitter">
                <FaTwitter /> Twitter
              </label>
              <div className="input-group">
                <div className="input-prefix">@</div>
                <input
                  type="text"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleChange}
                  placeholder="Your Twitter username (without @)"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="faceit">
                <SiFaceit /> FACEIT
              </label>
              <input
                type="text"
                id="faceit"
                name="faceit"
                value={formData.faceit}
                onChange={handleChange}
                placeholder="Your FACEIT username"
              />
            </div>
            
            <div className="form-checkbox">
              <input
                type="checkbox"
                id="lft"
                name="lft"
                checked={formData.lft}
                onChange={handleChange}
              />
              <label htmlFor="lft">
                I am currently looking for a team (LFT)
              </label>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => navigate(`/profile/${steamId}`)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="loading-spinner small"></div>
                    Saving...
                  </>
                ) : (
                  <>Save Changes</>
                )}
              </button>
            </div>
          </form>
          
          <div className="edit-profile-sidebar">
            <div className="sidebar-section">
              <h3>Profile Tips</h3>
              <ul className="tips-list">
                <li>Adding your Twitter and FACEIT usernames makes it easier for teams to contact you</li>
                <li>Toggle the LFT option to let others know you're available to join a team</li>
                <li>Your role helps teams find players that fit their needs</li>
                <li>You can change your avatar through Steam</li>
              </ul>
            </div>
            
            <div className="sidebar-section steam-info">
              <h3><FaSteam /> Steam Connection</h3>
              <p>Your profile is linked to your Steam account. Some information cannot be changed here.</p>
              <a 
                href={`https://steamcommunity.com/profiles/${steamId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="steam-link"
              >
                Visit Steam Profile
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;